import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HelpCircle, Search, ChevronDown, ChevronRight, PlayCircle,
  ClipboardCheck, FileCheck, FileSearch, CreditCard, Receipt,
  ShieldCheck, Phone, Bell, Users, Settings, FileText, Lock,
  UserCog, MessageSquare, BookOpen,
} from 'lucide-react';

/**
 * Help / FAQ page for Provider Portal.
 *
 * Renders a hero, "Quick start" CTA, search box, and an accordion of
 * topic cards. Each topic links to the relevant feature in the portal.
 */

const HelpPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);

  // Provider-specific help topics. Add new entries as the portal grows.
  const topics = useMemo(() => [
    {
      id: 'eligibility',
      icon: ClipboardCheck,
      color: '#0D9488',
      title: 'How do I check member eligibility and benefits?',
      body: 'Click "Eligibility & Benefits" in the left navigation. Search by Member ID, Subscriber ID, or by First Name + Last Name + Date of Birth. The result shows coverage status, plan type, copays, deductible/OOP totals, and PCP assignment. You can print or download the eligibility verification for your records.',
      action: { label: 'Open Eligibility', route: '/eligibility' },
    },
    //{
    //  id: 'auth-new',
    //  icon: FileCheck,
    //  color: '#7C3AED',
    //  title: 'How do I submit a new authorization request?',
    //  body: 'Go to "Authorizations" → "New Request". The 5-step wizard walks you through Member Search, Service Information (type, dates, requesting/servicing provider, facility), Procedures (CPT/HCPCS), Diagnosis (ICD-10), and Clinical Info (notes + attachments). On the final Review screen you can edit any step, download a PDF summary, or submit for review.',
    //  action: { label: 'Start a Request', route: '/authorizations' },
    //},
    //{
    //  id: 'auth-inquiry',
    //  icon: FileSearch,
    //  color: '#2563EB',
    //  title: 'How do I check the status of an authorization?',
    //  body: 'Open "Authorizations" → "Auth Inquiry". Filter by status (Approved / Pending / Denied / Cancelled), priority, date range, or member. Click the "+" on any row to expand the full detail panel — including procedures, diagnosis, requesting/servicing provider, decision date, and reviewer notes. Use Cards or Table view, and export results to Excel.',
    //  action: { label: 'Open Auth Inquiry', route: '/authorizations/inquiry' },
    //},
    {
      id: 'claims',
      icon: CreditCard,
      color: '#0F766E',
      title: 'How do I check claim status and payments?',
      body: 'Click "Claims & Payments" in the left navigation. Search by Member ID, Claim Number, Account Number, Check Number, or First Name + Last Name + DOB. Results show status, billed/paid amounts, and dates. Click any claim number to see line-item detail with CPT codes, allowed amounts, member responsibility, and adjudication notes. Switch between Cards or Table view and export to Excel.',
      action: { label: 'View Claims', route: '/claims' },
    },
    {
      id: 'eop',
      icon: Receipt,
      color: '#D97706',
      title: 'How do I find an Explanation of Payment (EOP)?',
      body: 'Go to "EOP Search". Search by EOP number, check number, or date range. Each EOP shows the payment date, check/EFT number, total amount, and the provider it was paid to. Click "Download" to grab the PDF for your records or accounting system.',
      action: { label: 'Search EOPs', route: '/eop' },
    },
    {
      id: 'forms',
      icon: FileText,
      color: '#6366F1',
      title: 'Where can I find forms and provider resources?',
      body: 'Open "Forms & Resources" from the left navigation. You\'ll find prior authorization forms, claim dispute forms, W-9 templates, provider manual sections, fee schedules, and coding references. All resources are downloadable PDFs with the latest revision date listed.',
      action: { label: 'Browse Forms', route: '/forms' },
    },
    {
      id: 'contact',
      icon: Phone,
      color: '#0D9488',
      title: 'How do I contact Provider Services?',
      body: 'Call Provider Services at (512) 901-9000 (Mon–Fri, 8 AM – 5 PM Central Time). For after-hours utilization-management requests, fax (512) 901-9001. The Contact & Resources page lists every department phone, fax, email, and mailing address.',
      action: { label: 'Open Contact Page', route: '/contact' },
    },
    {
      id: 'users',
      icon: Users,
      color: '#2563EB',
      title: 'How do I manage users in my organization?',
      body: 'If you are an Admin, click "User Management" in the sidebar. You can add new users, edit roles (Admin / Provider / Staff / Viewer), reset passwords, and deactivate accounts. Each role has different permissions — see the Role Permissions matrix on the Add User screen for details.',
      action: { label: 'User Management', route: '/admin/users' },
    },
    {
      id: 'add-user',
      icon: UserCog,
      color: '#7C3AED',
      title: 'How do I add a user?',
      body: 'Provider Admins can add users from "User Management" in the left navigation. Click "Add User", enter the person\'s name, email, phone, and username, choose a role, and set an initial password (use "Generate Password" for a strong one). Save to create the account and share the credentials securely — the user should change the password at first sign-in.',
      action: { label: 'Open User Management', route: '/admin/users' },
    },
    {
      id: 'change-password',
      icon: Lock,
      color: '#DC2626',
      title: 'How do I change my password?',
      body: 'If you are signed in: open "Profile" from the left navigation, go to the Security section, and choose "Change Password". Enter your current password, then the new password twice, and save. If you are locked out: use "Forgot Password?" on the sign-in screen to receive a reset link by email.',
      action: { label: 'Open Profile', route: '/profile' },
    },
    {
      id: 'add-tax-id',
      icon: FileText,
      color: '#0891B2',
      title: 'How do I add an additional Tax ID?',
      body: 'Provider Admins can manage Tax IDs from "Profile" in the left navigation. In the Tax IDs section, click "Add another Tax ID", enter the 9-digit TIN/EIN (duplicates are rejected), check the Tax ID attestation box to confirm the ID belongs to your organization, and click Save. Claims and EOPs for the new Tax ID appear once it is active on your account.',
      action: { label: 'Open Profile', route: '/profile' },
    },
    {
      id: 'profile',
      icon: Settings,
      color: '#0F766E',
      title: 'How do I update my profile and security settings?',
      body: 'Open "Profile" from the left navigation. You can edit your name, email, phone, and notification preferences. The Security tab lets you change your password and enable Multi-Factor Authentication (MFA) — strongly recommended for all users with PHI access.',
      action: { label: 'Open Profile', route: '/profile' },
    },
    {
      id: 'notifications',
      icon: Bell,
      color: '#F59E0B',
      title: 'How do I see my notifications?',
      body: 'Click the bell icon at the top right of any page or open "Notifications" from the menu. You\'ll see authorization decisions, claim status updates, payment alerts, and important system announcements. Use "Mark all as read" to clear the unread badge.',
      action: { label: 'Open Notifications', route: '/notifications' },
    },
    {
      id: 'security',
      icon: ShieldCheck,
      color: '#059669',
      title: 'How is provider and patient information protected?',
      body: 'The portal is HIPAA-compliant and SOC 2 Type II audited. Login uses Multi-Factor Authentication, all traffic is encrypted with TLS 1.3, sessions auto-expire after inactivity, and every action on member data is logged for audit. See the Privacy Policy for full details on data handling and retention.',
      action: { label: 'Read Privacy Policy', route: '/privacy' },
    },
    {
      id: 'session',
      icon: Lock,
      color: '#DC2626',
      title: 'Why was I signed out automatically?',
      body: 'For HIPAA compliance and to protect Protected Health Information (PHI), the portal automatically signs you out after a period of inactivity. You\'ll see a warning modal a couple of minutes before timeout where you can choose to stay signed in. Always sign out manually when you\'re done, especially on shared workstations.',
    },
    {
      id: 'roles',
      icon: UserCog,
      color: '#7C3AED',
      title: 'What can each user role do?',
      body: 'Super Admin: full system access including the System Users page across all organizations. Admin: manage users in their organization plus all clinical features. Provider: submit authorizations, view claims/eligibility, but cannot manage users. Staff: same as Provider plus cannot submit authorizations on their own. Viewer: read-only access for auditing or reporting.',
    },
    {
      id: 'browser',
      icon: BookOpen,
      color: '#6366F1',
      title: 'What browsers and devices are supported?',
      body: 'The portal works on the latest two versions of Chrome, Edge, Firefox, and Safari on desktop and mobile. We recommend Chrome or Edge for the best experience with file uploads in the authorization wizard. Mobile devices (iOS 15+ / Android 11+) are fully supported.',
    },
  ], []);

  const filtered = useMemo(() => {
    if (!search.trim()) return topics;
    const q = search.toLowerCase();
    return topics.filter((tp) =>
      tp.title.toLowerCase().includes(q) || tp.body.toLowerCase().includes(q),
    );
  }, [search, topics]);

  const startQuickStart = () => {
    // Trigger the guided tour (AppTour). Keys must match AppTour's constants.
    localStorage.setItem('sendero_tour_start', '1');
    localStorage.removeItem('sendero_tour_completed');
    window.dispatchEvent(new Event('sendero:start-tour')); // starts now if already on '/'
    navigate('/');                                         // else navigation starts it
  };

  return (
    <div style={{ maxWidth: 880, margin: '0 auto' }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #134E4A 0%, #0F766E 50%, #0D9488 100%)',
        borderRadius: 16, padding: '28px 32px', marginBottom: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative', flexWrap: 'wrap' }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HelpCircle size={22} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0, marginBottom: 4 }}>
              Help & Support
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, margin: 0 }}>
              Quick answers and step-by-step guidance for everything you can do in the Provider Portal.
            </p>
          </div>
        </div>
      </div>

      {/* Quick Start CTA */}
      <div
        onClick={startQuickStart}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); startQuickStart(); } }}
        style={{
          background: 'linear-gradient(135deg, rgba(13,148,136,0.06), rgba(99,91,255,0.06))',
          border: '1px solid rgba(13,148,136,0.20)',
          borderRadius: 14, padding: '18px 22px', marginBottom: 20,
          display: 'flex', alignItems: 'center', gap: 14,
          cursor: 'pointer', transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(13,148,136,0.10)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: 'linear-gradient(135deg, #0D9488, #6366F1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <PlayCircle size={26} color="#fff" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
            New here? Start with the dashboard
          </div>
          <div style={{ fontSize: 13, color: '#64748B', marginTop: 2 }}>
            {/*The home dashboard is the fastest way to see authorizations, claims, and recent activity at a glance.*/}
            The home dashboard is the fastest way to see claims, and recent activity at a glance.
          </div>
        </div>
        <ChevronRight size={20} color="#0D9488" />
      </div>

      {/* Search */}
      <div style={{
        background: '#fff', borderRadius: 14, border: '1px solid #E8ECF0',
        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
        marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <Search size={18} color="#94A3B8" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search help topics…"
          style={{
            flex: 1, border: 'none', outline: 'none', fontSize: 14,
            color: '#1E293B', fontFamily: 'inherit', background: 'transparent',
          }}
        />
      </div>

      {/* Section title */}
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#94A3B8',
        textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10,
      }}>
        Frequently asked
        <span style={{ color: '#0D9488', marginLeft: 8 }}>({filtered.length})</span>
      </div>

      {/* Accordion */}
      {filtered.length === 0 ? (
        <div style={{
          background: '#fff', borderRadius: 14, border: '1px solid #E8ECF0',
          padding: 32, textAlign: 'center', color: '#94A3B8', fontSize: 13,
        }}>
          No topics match your search.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((tp) => {
            const open = openId === tp.id;
            return (
              <div key={tp.id} style={{
                background: '#fff', borderRadius: 12,
                border: open ? `1px solid ${tp.color}40` : '1px solid #E8ECF0',
                boxShadow: open ? `0 4px 12px ${tp.color}10` : '0 1px 3px rgba(0,0,0,0.03)',
                overflow: 'hidden', transition: 'all 0.2s',
              }}>
                <button
                  onClick={() => setOpenId(open ? null : tp.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    gap: 14, padding: '14px 18px', background: 'transparent',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: `${tp.color}10`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <tp.icon size={18} color={tp.color} />
                  </div>
                  <div style={{
                    flex: 1, minWidth: 0,
                    fontSize: 14, fontWeight: 600, color: '#0F172A',
                  }}>
                    {tp.title}
                  </div>
                  <ChevronDown
                    size={18}
                    color="#94A3B8"
                    style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>
                {open && (
                  <div style={{
                    padding: '0 18px 18px 68px',
                    fontSize: 13, color: '#475569', lineHeight: 1.65,
                  }}>
                    <p style={{ margin: 0 }}>{tp.body}</p>
                    {tp.action && (
                      <button
                        onClick={() => navigate(tp.action.route)}
                        style={{
                          marginTop: 12, padding: '8px 14px',
                          background: tp.color, color: '#fff',
                          border: 'none', borderRadius: 8,
                          fontSize: 12, fontWeight: 700, cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        {tp.action.label} →
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer hint */}
      <div style={{
        marginTop: 24, padding: '14px 18px',
        background: 'rgba(13,148,136,0.04)',
        border: '1px solid rgba(13,148,136,0.15)',
        borderRadius: 12, fontSize: 13, color: '#0F766E',
        textAlign: 'center', display: 'flex', alignItems: 'center',
        justifyContent: 'center', gap: 8, flexWrap: 'wrap',
      }}>
        <Phone size={14} />
        Still need help? Call Provider Services at{' '}
              <a href="tel:512-978-8669" style={{ color: '#0D9488', fontWeight: 700, textDecoration: 'none' }}>
                  512-978-8669
        </a>
      </div>
    </div>
  );
};

export default HelpPage;
